// Galería de imágenes generadas por IA de la org.
// Cuadrícula de miniaturas + búsqueda por descripción + filtro por origen +
// favoritos. Lee de /admin/multimedia/gallery (choke point del backend).

import { useCallback, useEffect, useState } from 'react';
import { Search, Star, Copy, ExternalLink, ImageOff, Loader2 } from 'lucide-react';
import {
  listGallery,
  updateGalleryImage,
  SOURCE_LABELS,
  type GalleryImage,
} from './api';

type Notifier = (msg: string, kind?: 'success' | 'error' | 'info') => void;
interface Props {
  addNotification?: Notifier;
}

const SOURCES = ['', 'landing_hero', 'campaign', 'social', 'ai_images'] as const;

export function GalleryPage({ addNotification }: Props) {
  const [items, setItems] = useState<GalleryImage[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [source, setSource] = useState('');
  const [favOnly, setFavOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await listGallery({
        q: q.trim(),
        source,
        favorites: favOnly,
        limit: 60,
      });
      setItems(page.items);
      setTotal(page.total);
    } catch (e: any) {
      addNotification?.(e?.message || 'No se pudo cargar la galería', 'error');
    } finally {
      setLoading(false);
    }
  }, [q, source, favOnly, addNotification]);

  // Debounce de la búsqueda: no dispara un fetch por cada tecla.
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const toggleFavorite = async (img: GalleryImage) => {
    // Optimista: se refleja al instante y se revierte si el backend falla.
    const next = !img.favorite;
    setItems((prev) => prev.map((i) => (i.id === img.id ? { ...i, favorite: next } : i)));
    try {
      await updateGalleryImage(img.id, { favorite: next });
    } catch {
      setItems((prev) => prev.map((i) => (i.id === img.id ? { ...i, favorite: !next } : i)));
      addNotification?.('No se pudo actualizar el favorito', 'error');
    }
  };

  const copyUrl = (url: string) => {
    navigator.clipboard?.writeText(url);
    addNotification?.('URL copiada', 'success');
  };

  return (
    <div className="p-4 sm:p-6 space-y-5">
      {/* Encabezado */}
      <div>
        <h1 className="text-xl font-semibold text-foreground">Galería de imágenes</h1>
        <p className="text-sm text-muted-foreground">
          Todas las imágenes que la IA ha generado para tu organización — heros, campañas y contenido.
          {total > 0 && ` (${total})`}
        </p>
      </div>

      {/* Controles */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por descripción (ej. generador al atardecer)…"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-muted border border-border text-sm text-foreground outline-none focus:border-primary"
          />
        </div>

        <select
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className="py-2 px-3 rounded-lg bg-muted border border-border text-sm text-foreground outline-none"
        >
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {s === '' ? 'Todos los orígenes' : SOURCE_LABELS[s]}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => setFavOnly((v) => !v)}
          className={`flex items-center gap-1.5 py-2 px-3 rounded-lg border text-sm transition-colors ${
            favOnly
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-muted text-foreground border-border'
          }`}
        >
          <Star className="w-4 h-4" fill={favOnly ? 'currentColor' : 'none'} />
          Favoritas
        </button>
      </div>

      {/* Contenido */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <ImageOff className="w-8 h-8 mb-3 opacity-60" />
          <p className="text-sm">
            {q || source || favOnly
              ? 'Sin imágenes que coincidan con el filtro.'
              : 'Aún no se han generado imágenes para esta organización.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((img) => (
            <div
              key={img.id}
              className="group relative rounded-xl overflow-hidden border border-border bg-card"
            >
              {/* Miniatura con relación 4:3 estable (evita saltos de layout) */}
              <div className="aspect-[4/3] bg-muted overflow-hidden">
                <img
                  src={img.url}
                  alt={img.prompt || 'Imagen generada'}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
              </div>

              {/* Barra de acciones (aparece al pasar el cursor) */}
              <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => toggleFavorite(img)}
                  title={img.favorite ? 'Quitar de favoritas' : 'Marcar favorita'}
                  className="p-1.5 rounded-lg bg-background/80 backdrop-blur border border-border hover:bg-background"
                >
                  <Star
                    className={`w-4 h-4 ${img.favorite ? 'text-primary' : 'text-muted-foreground'}`}
                    fill={img.favorite ? 'currentColor' : 'none'}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => copyUrl(img.url)}
                  title="Copiar URL"
                  className="p-1.5 rounded-lg bg-background/80 backdrop-blur border border-border hover:bg-background"
                >
                  <Copy className="w-4 h-4 text-muted-foreground" />
                </button>
                <a
                  href={img.url}
                  target="_blank"
                  rel="noreferrer"
                  title="Abrir tamaño completo"
                  className="p-1.5 rounded-lg bg-background/80 backdrop-blur border border-border hover:bg-background"
                >
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </a>
              </div>

              {/* Estrella persistente cuando es favorita (visible sin hover) */}
              {img.favorite && (
                <div className="absolute top-2 left-2 group-hover:opacity-0 transition-opacity">
                  <Star className="w-4 h-4 text-primary" fill="currentColor" />
                </div>
              )}

              {/* Pie con origen + descripción corta */}
              <div className="p-2.5 space-y-1">
                <span className="inline-block text-[10px] uppercase tracking-wide text-muted-foreground">
                  {SOURCE_LABELS[img.source] || img.source}
                  {img.used_in ? ` · ${img.used_in}` : ''}
                </span>
                {img.prompt && (
                  <p className="text-xs text-foreground line-clamp-2" title={img.prompt}>
                    {img.prompt}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default GalleryPage;
